// Per-service persistent storage for the in-browser mock backend.
//
// Every service (users, tenants, bots, knowledge, conversations, leads, ...) keeps its own
// localStorage key, always read fresh and written through here, so:
//   - data survives refreshes and browser restarts (memory-persistent),
//   - two tabs (e.g. visitor widget + agent workspace) see each other's changes live,
//   - nothing outside src/api/mock ever touches these keys — UI code only talks to apiClient.
//
// When the Python backend replaces this mock, delete src/api/mock and set REACT_APP_USE_MOCK=false.

export const STORE_PREFIX = "chatbizz_svc_";
const SCHEMA_KEY = `${STORE_PREFIX}schema`;
// Bump when a seed/record shape changes so stale cached data from an older build is discarded.
const SCHEMA_VERSION = "5";

export const STORE_NAMES = [
  "users", "tenants", "sessions", "codes", "profiles", "bots", "knowledge", "imports",
  "conversations", "leads", "webhooks", "domains", "marketing", "settings", "tenantKeys", "aiUsage", "businessHours"
];

function storage() {
  try { return window.localStorage; } catch (e) { return null; }
}

(function resetIfSchemaChanged() {
  const ls = storage();
  if (!ls) return;
  try {
    if (ls.getItem(SCHEMA_KEY) !== SCHEMA_VERSION) {
      STORE_NAMES.forEach((name) => ls.removeItem(`${STORE_PREFIX}${name}`));
      ls.setItem(SCHEMA_KEY, SCHEMA_VERSION);
    }
  } catch (e) { /* storage unavailable */ }
})();

const clone = (value) => JSON.parse(JSON.stringify(value));

/** Reads a service store. First access seeds it (seed may be a value or a function). */
export function readStore(name, seed) {
  const ls = storage();
  const key = `${STORE_PREFIX}${name}`;
  if (ls) {
    try {
      const raw = ls.getItem(key);
      if (raw !== null) return JSON.parse(raw);
    } catch (e) { /* fall through to seed */ }
  }
  const initial = clone(typeof seed === "function" ? seed() : seed);
  writeStore(name, initial);
  return initial;
}

/** Writes a service store. Returns false when storage is full/unavailable. */
export function writeStore(name, value) {
  const ls = storage();
  if (!ls) return false;
  try { ls.setItem(`${STORE_PREFIX}${name}`, JSON.stringify(value)); return true; } catch (e) { return false; }
}

/** Removes every mock service store (used by tests and the "reset demo data" helper). */
export function resetAllStores() {
  const ls = storage();
  if (!ls) return;
  STORE_NAMES.forEach((name) => ls.removeItem(`${STORE_PREFIX}${name}`));
}
